import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FiCheck, FiAlertTriangle, FiRefreshCw, FiCopy, FiChevronDown, FiZap, FiTerminal } from 'react-icons/fi';
import { useRunDetailPanel } from '../../context/RunDetailPanelContext';
import { useTheme } from '../../context/ThemeContext';
import { summarizeToolStep, humanizeKey } from '../../utils/toolSteps';
import '../../styles/RunDetailSidebarPanel.css';

const INTERNAL_URL_RE = /https?:\/\/\S*null-ref-express\.nowcerts\.com\S*/g;
function maskInternalUrls(text: string): string {
  return text.replace(INTERNAL_URL_RE, '[internal]');
}
function tryParseJson(str: string): any | null {
  try { return JSON.parse(str); } catch { return null; }
}
function formatInputValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
}
const RunDetailSidebarPanel: React.FC = () => {
  const { runDetailPanel } = useRunDetailPanel();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [expandedExecs, setExpandedExecs] = useState<Set<number>>(new Set());
  const [rawOpen, setRawOpen] = useState<Set<number>>(new Set());

  if (!runDetailPanel) return null;

  const { execs, exeLoading } = runDetailPanel;

  const handleCopy = (text: string, idx: number) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(idx);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  const toggleExec = (idx: number) => {
    setExpandedExecs(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleRaw = (idx: number) => {
    setRawOpen(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const mdCfg: any = {
    p:      ({ children }: any) => <p style={{ margin: '6px 0', color: isLight ? '#334155' : 'rgba(255,255,255,0.74)', fontSize: 13, lineHeight: 1.6 }}>{children}</p>,
    h1:     ({ children }: any) => <h1 style={{ margin: '10px 0 6px', fontWeight: 700, fontSize: 15, lineHeight: 1.35, color: isLight ? '#0f172a' : 'rgba(255,255,255,0.92)' }}>{children}</h1>,
    h2:     ({ children }: any) => <h2 style={{ margin: '9px 0 5px', fontWeight: 700, fontSize: 15, lineHeight: 1.4, color: isLight ? '#111827' : 'rgba(255,255,255,0.9)' }}>{children}</h2>,
    h3:     ({ children }: any) => <h3 style={{ margin: '8px 0 5px', fontWeight: 700, fontSize: 13, lineHeight: 1.45, color: isLight ? '#1f2937' : 'rgba(255,255,255,0.86)' }}>{children}</h3>,
    h4:     ({ children }: any) => <h4 style={{ margin: '7px 0 4px', fontWeight: 700, fontSize: 13, lineHeight: 1.45, color: isLight ? '#374151' : 'rgba(255,255,255,0.84)' }}>{children}</h4>,
    h5:     ({ children }: any) => <h5 style={{ margin: '6px 0 4px', fontWeight: 700, fontSize: 13, lineHeight: 1.45, color: isLight ? '#475569' : 'rgba(255,255,255,0.8)' }}>{children}</h5>,
    h6:     ({ children }: any) => <h6 style={{ margin: '6px 0 4px', fontWeight: 700, fontSize: 11, lineHeight: 1.4, color: isLight ? '#64748b' : 'rgba(255,255,255,0.72)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</h6>,
    ul:     ({ children }: any) => <ul style={{ margin: '6px 0', paddingLeft: 18 }}>{children}</ul>,
    ol:     ({ children }: any) => <ol style={{ margin: '6px 0', paddingLeft: 18 }}>{children}</ol>,
    li:     ({ children }: any) => <li style={{ margin: '4px 0', color: isLight ? '#334155' : 'rgba(255,255,255,0.72)', fontSize: 13, lineHeight: 1.6 }}>{children}</li>,
    a:      ({ href, children }: any) => <a href={href} target="_blank" rel="noreferrer" style={{ color: '#8b5cf6', textDecoration: 'underline' }}>{children}</a>,
    strong: ({ children }: any) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
    em:     ({ children }: any) => <em>{children}</em>,
    code:   ({ children }: any) => <code style={{ background: isLight ? '#e2e8f0' : 'rgba(0,0,0,0.22)', padding: '1px 6px', borderRadius: 4, fontFamily: 'monospace', fontSize: 11, color: isLight ? '#334155' : 'rgba(255,255,255,0.72)' }}>{children}</code>,
    blockquote: ({ children }: any) => (
      <blockquote
        style={{
          margin: '8px 0',
          padding: '6px 10px',
          borderLeft: `3px solid ${isLight ? 'rgba(124,58,237,0.45)' : 'rgba(167,139,250,0.5)'}`,
          background: isLight ? 'rgba(124,58,237,0.05)' : 'rgba(167,139,250,0.08)',
          borderRadius: 6,
          color: isLight ? '#334155' : 'rgba(255,255,255,0.74)',
        }}
      >
        {children}
      </blockquote>
    ),
    table: ({ children }: any) => (
      <div style={{ margin: '8px 0', overflowX: 'auto', border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.12)'}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }}>{children}</table>
      </div>
    ),
    thead: ({ children }: any) => <thead style={{ background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)' }}>{children}</thead>,
    tbody: ({ children }: any) => <tbody>{children}</tbody>,
    tr: ({ children }: any) => <tr style={{ borderTop: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}` }}>{children}</tr>,
    th: ({ children }: any) => (
      <th
        style={{
          padding: '7px 9px',
          textAlign: 'left',
          fontSize: 11,
          fontWeight: 700,
          color: isLight ? '#334155' : 'rgba(255,255,255,0.84)',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </th>
    ),
    td: ({ children }: any) => (
      <td
        style={{
          padding: '7px 9px',
          fontSize: 11,
          lineHeight: 1.5,
          color: isLight ? '#475569' : 'rgba(255,255,255,0.72)',
          verticalAlign: 'top',
          wordBreak: 'break-word',
        }}
      >
        {children}
      </td>
    ),
    hr:     () => <hr style={{ border: 'none', borderTop: `1px solid ${isLight ? '#e5e7eb' : 'rgba(255,255,255,0.07)'}`, margin: '7px 0' }} />,
  };

  const failedCount = execs ? execs.filter(e => !summarizeToolStep(e).ok).length : 0;

  return (
    <div className="run-steps">

      {/* ══ Summary header — plain English, counts only ══ */}
      <div className="run-steps__head">
        <div className="run-steps__head-title">
          <FiZap size={13} aria-hidden="true" />
          What the agent did
        </div>
        {execs && execs.length > 0 && (
          <div className={`run-steps__head-meta${failedCount ? ' run-steps__head-meta--warn' : ''}`}>
            {execs.length} {execs.length === 1 ? 'step' : 'steps'}
            <span aria-hidden="true"> · </span>
            {failedCount === 0 ? 'all went through' : `${failedCount} had a problem`}
          </div>
        )}
      </div>

      <div className="run-steps__scroll">
        {exeLoading ? (
          <div className="run-steps__empty">
            <FiRefreshCw size={14} className="run-steps__spin" aria-hidden="true" /> Loading steps…
          </div>
        ) : !execs || execs.length === 0 ? (
          <div className="run-steps__empty run-steps__empty--stack">
            <span className="run-steps__empty-icon"><FiZap size={16} aria-hidden="true" /></span>
            No steps recorded for this reply.
          </div>
        ) : (
          <ol className="run-steps__list">
            {execs.map((exec, ei) => {
              const step = summarizeToolStep(exec);
              const parsedAction = tryParseJson(exec.action);
              const parsedResult = tryParseJson(exec.result);
              const isEmptyResult =
                !exec.result?.trim() ||
                (parsedResult !== null && typeof parsedResult === 'object' &&
                  (Array.isArray(parsedResult) ? parsedResult.length === 0 : Object.keys(parsedResult).length === 0));
              const inputs = parsedAction && typeof parsedAction === 'object' && !Array.isArray(parsedAction)
                ? Object.entries(parsedAction) : [];
              const isExpanded = expandedExecs.has(ei);
              const showRaw = rawOpen.has(ei);
              const tone = step.running ? 'running' : step.ok ? 'ok' : 'fail';

              return (
                <li key={ei} className={`run-step run-step--${tone}${isExpanded ? ' run-step--open' : ''}`}>
                  <button
                    type="button"
                    className="run-step__row"
                    onClick={() => toggleExec(ei)}
                    aria-expanded={isExpanded}
                  >
                    <span className="run-step__icon" aria-hidden="true">
                      {step.running ? <FiRefreshCw size={12} className="run-steps__spin" />
                        : step.ok ? <FiCheck size={12} /> : <FiAlertTriangle size={12} />}
                    </span>
                    <span className="run-step__text">
                      <span className="run-step__title">{step.title}</span>
                      <span className="run-step__outcome">{step.outcome}</span>
                    </span>
                    <FiChevronDown size={14} className="run-step__chevron" aria-hidden="true" />
                  </button>

                  {/* ── Technical details — the actual tool / MCP call ── */}
                  {isExpanded && (
                    <div className="run-step__details">
                      <dl className="run-step__facts">
                        <dt>Step</dt>
                        <dd>{ei + 1} of {execs.length}</dd>
                        {step.source && (<><dt>Connected app</dt><dd>{step.source}</dd></>)}
                        <dt>Tool</dt>
                        <dd><code className="run-step__code">{exec.tool}</code></dd>
                        <dt>Status</dt>
                        <dd>{step.running ? 'Running' : step.ok ? 'Succeeded' : 'Failed'}{exec.status ? ` (${exec.status})` : ''}</dd>
                      </dl>

                      {inputs.length > 0 && (
                        <section className="run-step__section">
                          <div className="run-step__label">What it sent</div>
                          <dl className="run-step__kv">
                            {inputs.map(([k, v]) => (
                              <React.Fragment key={k}>
                                <dt title={k}>{humanizeKey(k)}</dt>
                                <dd>{maskInternalUrls(formatInputValue(v)) || '—'}</dd>
                              </React.Fragment>
                            ))}
                          </dl>
                        </section>
                      )}

                      <section className="run-step__section">
                        <div className="run-step__label-row">
                          <div className="run-step__label">What came back</div>
                          {!isEmptyResult && (
                            <button type="button" className="run-step__mini-btn" onClick={() => handleCopy(exec.result, ei)}>
                              {copiedId === ei ? <FiCheck size={11} /> : <FiCopy size={11} />}
                              {copiedId === ei ? 'Copied' : 'Copy'}
                            </button>
                          )}
                        </div>
                        <div className="run-step__result">
                          {isEmptyResult ? (
                            <span className="run-step__muted">Nothing came back.</span>
                          ) : parsedResult && typeof parsedResult === 'object' ? (
                            <span>{step.outcome}.</span>
                          ) : (
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdCfg}>
                              {maskInternalUrls(exec.result)}
                            </ReactMarkdown>
                          )}
                        </div>
                      </section>

                      {(exec.action?.trim() || exec.result?.trim()) && (
                        <>
                          <button type="button" className="run-step__raw-toggle" onClick={() => toggleRaw(ei)} aria-expanded={showRaw}>
                            <FiTerminal size={12} aria-hidden="true" />
                            {showRaw ? 'Hide raw call' : 'View raw call'}
                          </button>
                          {showRaw && (
                            <div className="run-step__raw">
                              {exec.action?.trim() && (
                                <>
                                  <div className="run-step__label">Request</div>
                                  <pre>{maskInternalUrls(parsedAction ? JSON.stringify(parsedAction, null, 2) : exec.action)}</pre>
                                </>
                              )}
                              {exec.result?.trim() && (
                                <>
                                  <div className="run-step__label">Response</div>
                                  <pre>{maskInternalUrls(parsedResult ? JSON.stringify(parsedResult, null, 2) : exec.result)}</pre>
                                </>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
};

export default RunDetailSidebarPanel;
